<template>
	
	<div class="mb-3">
		<div class="font-weight-medium pt-0 mb-3">{{field.name}}</div>
		<div v-show="uploadProgress < 1 && !filesIsUploaded">
			<div class="body-2 pt-0 mb-3">Please make sure that the video you upload are HTML compatible (typically encoded in H.264/MP4 format).</div>
			<v-form ref="uploadForm">
				<v-row class="mb-3">
					<v-col cols="12" md="6">

						<v-file-input
							:rules="agile.fieldRules.requiredRule"
							ref="imageFileInput"
							label="Click to select video preview image"
							prepend-icon="mdi-image"
							outlined dense
							accept="image/png, image/jpeg"
							v-model="imageValue"
						></v-file-input>

					</v-col>
					<v-col cols="12" md="6">

						<v-file-input
							:rules="agile.fieldRules.requiredRule"
							ref="videoFileInput"
							label="Click to select video file"
							prepend-icon="mdi-movie"
							outlined
							dense
							accept="video/*"
							v-model="videoValue"
						></v-file-input>

					</v-col>

				</v-row>
				<div class="text-center">
					<v-btn class="ml-9 mt-n4 elevation-0 blue" width="200" dark rounded @click="uploadFiles">Upload files</v-btn>
				</div>
				<v-row>
					<v-col>
						
					</v-col>
				</v-row>
			</v-form>
		</div>

		<div class="text-center" v-if="uploadProgress > 0 && !filesIsUploaded">
			<v-progress-circular
				:rotate="-90"
				:size="100"
				:width="15"
				:value="uploadProgress"
				:color="uploadError ? '#961919' : 'primary'"
				class="mb-3"
			>
			{{ uploadProgress }}
			</v-progress-circular>
			<div class="body-2">{{ (uploadError) ? 'Error: '+uploadError  : 'Uploading files, please stay in this page until upload is finished.' }}</div>
			
			<v-btn v-if="uploadError" text small @click="uploadError = ''; uploadFiles();">Try again</v-btn>
			<v-btn v-if="uploadError" text small @click="uploadError = ''; uploadProgress = 0">Back</v-btn>

		</div>

		<div v-if="filesIsUploaded === true" class="d-flex flex-row">
			<v-img
				:src="previewImageSrc"
				width="250"
				height="125"
				style="max-width: 250px;"
				class="grey darken-4"
            ></v-img>
			<v-btn class="ml-3" @click="resetAll">Remove</v-btn>
			<v-spacer/>
		</div>

	</div>

</template>



<script>



// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

//import agile from "@/shift/agile";
import fb from '@/shift/fb';
import agile from "@/shift/agile";
import AiImageFileReader from "@/shift/AiImageFileReader";
import AiVideoMetadataReader from "@/shift/AiVideoMetadataReader";


export default
{
	name: 'ContentFieldSimpleVideo',


	// ----------------------------------
	//	VUE: Data, properties, etc.
	// ----------------------------------

	props:
	{
		field: { type: Object },
	},

	data: () =>
	({
		imageValue: null,
		videoValue: null,
		uploadError: '',
		uploadProgress: 0,
		userSelectedImage: null,
		userSelectedVideo: null,
		previewImageSrc: '',
		filesIsUploaded: false,
		imageFileReader: null,
		videoMetadataReader: null,
		uploads: null,
	}),

	computed:
	{
        agile()
        {
            return agile;
		},
	},

	// ----------------------------------------------------
	//
	//  VUE: Create and Mount
	//
	// ----------------------------------------------------

	created()
	{
		if (this.field.data && this.field.data.previewImage)
		{
			const dataValue = this.field.data;

			if (dataValue)
			{
				this.field.value = dataValue;
				this.setImageSrc();
			}
			
		}
		
		this.imageFileReader = new AiImageFileReader();
		this.videoMetadataReader = new AiVideoMetadataReader();
		
		//https://firebasestorage.googleapis.com/v0/b/agile-shift-genesis.appspot.com/o/SunCloud.jpg?alt=media
	},

	mounted()
	{
		this.$refs.imageFileInput.$on('change', this.onImageFileChosen);
		this.$refs.videoFileInput.$on('change', this.onVideoFileChosen);
	},


	// ----------------------------------------------------
	//
	//  VUE: Methods
	//
	// ----------------------------------------------------

	methods:
	{
		resetAll()
		{
			this.filesIsUploaded = false;
			this.uploadProgress = 0;
			
			this.imageValue = null;
			this.videoValue = null;

			this.$refs.uploadForm.resetValidation();
			
			this.field.value = {};
		},

		resetUpload()
		{
			this.filesIsUploaded = false;
			this.uploadProgress = 0;
		},

		// ----------------------------------
		//	When user has browsed for video
		// ----------------------------------

		onVideoFileChosen(file)
		{
			this.userSelectedVideo = null;
			if (!file) return;

			this.videoMetadataReader.read(file)
				.then((videoElement) =>
				{
					// Prepend file with 5 random number to prevent file name collision
					const path = 'content/'+this.field.actualId+'/'+agile.random5DigitString()+file.name;
					
					this.userSelectedVideo = 
					{
						file: file,
						url: encodeURIComponent(path),
						path: path,
						size: file.size,
						width: videoElement.videoWidth,
						height: videoElement.videoHeight,
					};
					
				})
				.catch((er) =>
				{
					this.$root.$alert('Error with file', 'Something is wrong with the file :-(');
					console.log('Error with media file reader', er);
				});
		},



		// ----------------------------------
		//	When user has browsed for image
		// ----------------------------------

		onImageFileChosen(file)
		{
			this.userSelectedImage = null;
			if (!file) return;
			

			this.imageFileReader.read(file)
				.then((image) =>
				{
					const path = 'content/'+this.field.actualId+'/'+agile.random5DigitString()+file.name;

					this.userSelectedImage = 
					{
						file: file,
						url: encodeURIComponent(path),
						path: path,
						size: file.size,
						width: image.width,
						height: image.height,
					}

				})
				.catch((er) =>
				{
					this.$root.$alert('Error with file', 'Something is wrong with the file :-(');
					console.log('Error with media file reader', er);
				});
		},



		uploadFiles()
		{
			if (this.$refs.uploadForm.validate())
			{
				if (this.userSelectedVideo && this.userSelectedImage)
				{
					const createUpload = (path, file) =>
					{
						const upload = {};
						upload.storageRef = fb.storage.ref(path);
						upload.task = upload.storageRef.put(file);

						upload.percent = 0;
						upload.complete = false;
						upload.error = false;
						
						upload.bytesTransferred = 0;
						upload.totalBytes = file.size;

						upload.onProgressProxy = (snapshot) => { this.onProgress(snapshot, upload) };
						upload.onErrorProxy = (er) => { this.onError(er, upload) };
						upload.onCompleteProxy = () => { this.onComplete(upload) };

						upload.task.on('state_changed', upload.onProgressProxy, upload.onErrorProxy, upload.onCompleteProxy);
						return upload;
					}

					this.uploads = 
					{
						image: createUpload(this.userSelectedImage.path, this.userSelectedImage.file),
						video: createUpload(this.userSelectedVideo.path, this.userSelectedVideo.file),
					};

				}
				else
				{
					this.$root.$alert('Error in field', 'Some file seems to be invalid.');
				}
				
			}		
		},



		onProgress(snapshot, upload)
		{
			upload.bytesTransferred = snapshot.bytesTransferred;
			upload.totalBytes = snapshot.totalBytes;

			const bytesTransferred = this.uploads.image.bytesTransferred+this.uploads.video.bytesTransferred;
			const totalBytes = this.uploads.image.totalBytes+this.uploads.video.totalBytes;
			
			this.uploadProgress = Math.round((bytesTransferred / totalBytes)*100);
		},



		onError(err, upload)
		{
			upload.error = true;
			this.uploadError = err;

			this.uploadProgress = 1;

			this.uploads.image.task.cancel();
			this.uploads.video.task.cancel();

			//console.log('File upload error:', upload, err);
		},



		async onComplete(upload)
		{
			upload.complete = true;
			
			if (this.uploads.image.complete && this.uploads.video.complete)
			{
				// Set values for database

				// We don't want to pass the file objects to firestore, delete them first
				delete this.userSelectedImage.file;
				delete this.userSelectedVideo.file;
				
				// The rest of the data is ok to have
				this.field.value = {};

				// Get downloadable url's with tokens in it
				const promises = [];

				promises.push(
					this.uploads.image.storageRef.getDownloadURL()
					.then((url) =>
					{
						this.field.value.previewImage = this.userSelectedImage;
						this.field.value.previewImage.url = url;
						return 'good';
					}).catch(function(err) {
						return err;
					})
				);

				promises.push(
					this.uploads.video.storageRef.getDownloadURL()
					.then((url) =>
					{
						this.field.value.video = this.userSelectedVideo;
						this.field.value.video.url = url;
						return 'good';
					}).catch(function(err) {
						return err;
					})
				);


				const userUpdateResults = await Promise.all(promises);

				for (let i = 0; i < userUpdateResults.length; i++)
				{
					if (userUpdateResults[i] !== 'good')
					{
						this.uploadError = userUpdateResults[i];
					}
				}

				//console.log("Happendos");
				this.setImageSrc();

			}

		},



		setImageSrc()
		{
			this.previewImageSrc = this.field.value.previewImage.url;//'https://firebasestorage.googleapis.com/v0/b/hofseth-ae6f4.appspot.com/o/'+this.field.value.previewImage.url+'?alt=media';
			this.filesIsUploaded = true;
			this.uploadProgress = 0;
		}



	},


	beforeDestroy()
	{
		
		this.$refs.imageFileInput.$off('change', this.onImageFileChosen);
		this.$refs.videoFileInput.$off('change', this.onVideoFileChosen);
	},

}

</script>

